import React, { useState, useEffect, useImperativeHandle, ReactNode, forwardRef, useContext } from "react";
import classnames from 'classnames';
import './MarketFilters.scss';
import { FilterGroup, FilterSection } from "@/components/ConfigurationEditors/FilterGroup";
import { MarketRunConfigOptions } from "@/types/RunConfigOptions";
import { DefaultConfigWizardContextValue, DefaultConfigWizardContext, DefaultConfigWizardState } from "../../DefaultsConfigWizard/DefaultsConfigWizardContext";
import { useUser } from "@/contexts/UserContext";
import ByzzerMarketSearch from "@/components/MarketSelection/ByzzerMarketSearch/ByzzerMarketSearch";
import { MarketPicker } from "@/components/MarketPicker";
import { DefaultConfigLabels } from "@/pages/Onboarding/OnboardingSteps";
import { OmniMarketSelect } from "@/components/OmniMarketSelect";
import { ByzzerOshMarketSelect } from "@/components/ByzzerOshMarketSelect";

export type MarketFiltersRef = {
    value?: DefaultConfigWizardContextValue['value'];
}

export type MarketFiltersProps = {
    name?: string;
    onChange?: (e: ByzzerChangeEvent<DefaultConfigWizardContextValue['value']>) => void;
    value?: DefaultConfigWizardContextValue['value'];
    summary?: ReactNode;
    labelsAndTips?: DefaultConfigLabels;
} & Partial<Omit<MarketRunConfigOptions, 'type' | 'title'>> 
  & Partial<Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>>;

export const MarketFilters = forwardRef<MarketFiltersRef, MarketFiltersProps>((
    {
        className,
        includeFmcgRetailers,
        includeGeographyMarkets,
        includeSpecialityRetailers,
        includeTotalUSMarkets,
        includeOmniTotalMarkets,
        includeOmniSubMarkets,
        includeOmniShopperMarkets,
        limitMarketsToParentCompany,
        maxMarkets,
        maxOmniTotalMarkets,
        maxOmniSubMarkets,
        maxOmniShopperMarkets,
        name,
        value,
        onChange,
        labelsAndTips,
        summary = labelsAndTips?.markets_header,
        ...props
    }, ref) => {

    const baseClassName = 'byz-market-filters';

    const { value: contextValue, onChange: onContextChange, state: contextState, selectedDataset } = useContext(
        DefaultConfigWizardContext
    );

    const [internalValue, setInternalValue] = useState<DefaultConfigWizardContextValue['value']>({});

    const [state, setState] = useState<DefaultConfigWizardState>({
        // enableMarketTypePicker: false,
        // showRemainingMarkets: false
    });

    const { company, features: userFeatures = {}, defaultRunConfig, categories: subscriptionCategories } = useUser();

    function handleChange(e: ByzzerChangeEvent<unknown>) {
        onContextChange?.(e.name as keyof typeof defaultRunConfig, e.value, e.data);
    }

    useImperativeHandle(ref, () => ({
        get value() {
            return internalValue;
        },
    }));

    useEffect(() => {
        setInternalValue(contextValue ?? {});
    }, [contextValue]);

    useEffect(() => {
        if (value) {
            setInternalValue(value);
        }
    }, [value]);

    useEffect(() => {

        const firstMarket = contextValue.markets?.[0];
        const requiredMasterCompany = limitMarketsToParentCompany ? firstMarket?.masterCompany : undefined;
        const requiredMarketGroup = firstMarket?.marketGroup;

        setState(state => ({
            ...state,
            requiredMasterCompany,
            requiredMarketGroup,
        }));
    }, [contextValue.markets]);
    
    const categoriesRefreshKey = `${contextValue.categories?.sort().join()}`

    const categories = Boolean(!contextValue.categories?.length) ? subscriptionCategories : contextValue.categories;

    return (
        <FilterGroup className={classnames(baseClassName, className)}>
            {summary && <div className={`${baseClassName}__summary`}>{summary}</div>}
            <FilterSection
                onlyRenderIf={selectedDataset === 'rms' || selectedDataset === 'cps'}
            >
                <ByzzerMarketSearch
                    name={'markets'}
                    productSku={selectedDataset === 'cps' ? '790' : '57'} // TODO: Need to revisit this because on the basis of sku it is rendering markets.
                    categories={categories}
                    requiredMasterCompany={state.requiredMasterCompany}
                    requiredMarketGroup={state.requiredMarketGroup}
                    showRemainingMarkets={state.showRemainingMarkets}
                    value={contextValue.markets}
                    maxSelections={maxMarkets}
                    onChange={handleChange}
                />
                    
                <MarketPicker
                    key={categoriesRefreshKey}
                    value={contextValue.markets}
                    onChange={handleChange}
                    maxSelections={maxMarkets}
                    categories={categories}
                    requiredMasterCompany={state?.requiredMasterCompany}
                    requiredMarketGroup={state?.requiredMarketGroup}
                    name={'markets'}
                    productSku={selectedDataset === 'cps' ? '790' : '57'} // TODO: Need to revisit this because on the basis of sku it is rendering markets.
                    enableParentMarket={selectedDataset === 'cps'}
                    showRemainingMarkets={state.showRemainingMarkets}
                    displayMarketGroup={selectedDataset}
                    purchasedMarketKeys={company?.purchasedMarketKeys}
                    filterType="all"
                />
            </FilterSection>

            {/* Omni Markets */}
            <FilterSection onlyRenderIf={selectedDataset === 'omni' && includeOmniTotalMarkets}>
                <OmniMarketSelect
                    name={'markets'}
                    value={contextValue.markets}
                    onChange={handleChange}
                    placeholder={'Select Total Markets'}
                    categories={categories!}
                    runType={'subscription'}
                    allowMultiple={Number(maxOmniTotalMarkets) > 0}
                    maxSelections={maxOmniTotalMarkets}
                    label={'Select the total market(s)'}
                />
            </FilterSection>

            <FilterSection onlyRenderIf={selectedDataset === 'omni' && includeOmniSubMarkets}>
                <OmniMarketSelect
                    name={"subMarkets"}
                    value={contextValue.subMarkets}
                    onChange={handleChange}
                    placeholder={"Select Sub-Markets"}
                    categories={categories!}
                    runType={'subscription'}
                    allowMultiple={Number(maxOmniSubMarkets) > 0}
                    maxSelections={maxOmniSubMarkets}
                    label={'Select the submarket(s)'}
                />
            </FilterSection>

            {/* Omnishopper Markets */}
            <FilterSection onlyRenderIf={selectedDataset === 'osh' && includeOmniShopperMarkets}>
                <ByzzerOshMarketSelect
                    includeOmniShopperMarkets={true}
                    sku={'952'} // TODO: Need to modify the Market component because it is relying on sku to render labels.
                    value={contextValue.markets ?? []}
                    onChange={handleChange}
                    name={'markets'}
                    maxSelections={maxOmniShopperMarkets}
                />
            </FilterSection>
        </FilterGroup>
    );

});

export default MarketFilters;

MarketFilters.displayName = 'MarketFilters';
